/**
 * Operating-dashboard section cards (Reports Phase B.2, migration 0153).
 *
 * The founder's richer PDF operating dashboard, native in Peace OS. Each card
 * renders ONLY when its data is present — an absent section hides its card and
 * the page degrades gracefully to the core weekly view. Charts are hand-rolled
 * (div bars + inline-SVG polylines), matching the page's existing idiom — no
 * charting dependency.
 *
 * REGISTER COLOR LAW (founder dashboard): forest (sage-800) = CM / primary;
 * gold (warm-400 / warm-700 for small text) = SPICE / plan / secondary AND the
 * hours attention signal (a clinician under/over their contracted target);
 * pale sage (sage-300) = prior year / ambient. Pepper is NOT used here — a
 * clinician off their target hours is an attention matter (gold), not an
 * alarm (the caution register + pepper stay reserved per
 * docs/reference/design-language.md).
 */

import { type ReactNode } from 'react';
import type {
  WeeklyOperatingMetricWire,
  OperatingTopClient,
  OperatingRosterTarget,
  OperatingMonthlyPoint,
} from '@/lib/api';
import { Chip } from '@/components/primitives/Chip';
import {
  money,
  moneyNum,
  hours,
  signedPct,
  formatWeekEnd,
  monthLabel,
  weeklySeriesStats,
  clinicianAggregates,
  buildTargetLookup,
  buildClinicianRows,
  sortCmRows,
  sortSpiceRows,
  type ClinicianAgg,
  type ClinicianFlag,
  type ClinicianTableRow,
} from './operatingDerive';

const FOREST = 'var(--color-sage-800, #1B4332)';
const GOLD = 'var(--color-warm-400, #C4A265)';
const PALE = 'var(--color-sage-300, #A3B8B0)';

const CARD = 'bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4';

function Card({ testId, title, children }: { testId: string; title: string; children: ReactNode }): ReactNode {
  return (
    <div data-testid={testId} className={CARD}>
      <h2 className="font-display text-lg text-sage-800 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function LegendDot({ color, label, dash }: { color: string; label: string; dash?: boolean }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1">
      {dash ? (
        <span className="inline-block h-0 w-3 border-t-2 border-dashed" style={{ borderColor: color }} />
      ) : (
        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      )}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------
// Polyline helpers (shared inline-SVG idiom)
// ---------------------------------------------------------------------

/** Build a polyline points string over a 0..100 box for a numeric series
 * (nulls are skipped), given the shared [min,max] domain. */
function points(series: Array<number | null>, min: number, max: number): string {
  const span = max - min || 1;
  const n = series.length;
  return series
    .map((v, i) => {
      if (v === null) return null;
      const x = n === 1 ? 50 : (i / (n - 1)) * 100;
      const y = 100 - ((v - min) / span) * 100;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .filter((p): p is string => p !== null)
    .join(' ');
}

/** A flat reference line (e.g. a 12-week average) across the 0..100 box. */
function flatLine(value: number, min: number, max: number): string {
  const span = max - min || 1;
  const y = (100 - ((value - min) / span) * 100).toFixed(2);
  return `0,${y} 100,${y}`;
}

// ---------------------------------------------------------------------
// Weekly bar chart (CM or SPICE) + 4-wk-avg line + dashed 12-wk-avg
// ---------------------------------------------------------------------

export function WeeklyBarChart({
  title,
  rows,
  pick,
  color,
  testId,
}: {
  title: string;
  rows: WeeklyOperatingMetricWire[];
  pick: (r: WeeklyOperatingMetricWire) => string | null;
  color: string;
  testId: string;
}): ReactNode {
  const chrono = [...rows].reverse();
  const stats = weeklySeriesStats(rows, pick);
  const max = stats.series.reduce<number>((m, v) => (v !== null ? Math.max(m, v) : m), 0);

  // 4-week trailing-average line (right-aligned running mean of present vals).
  const runningAvg4: Array<number | null> = stats.series.map((_, i) => {
    const window: number[] = [];
    for (let j = Math.max(0, i - 3); j <= i; j++) {
      const v = stats.series[j];
      if (v !== null) window.push(v);
    }
    return window.length ? window.reduce((s, x) => s + x, 0) / window.length : null;
  });

  const avg4Points = points(runningAvg4, 0, max);
  const avg12Points = stats.avg12 !== null ? flatLine(stats.avg12, 0, max) : '';

  return (
    <div data-testid={testId} className={CARD}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-sage-800">{title}</h2>
        <div className="flex items-center gap-3 text-[11px] text-ink-soft">
          <LegendDot color={color} label="Weekly" />
          <LegendDot color={color} label="4-wk avg" />
          <LegendDot color={PALE} label="12-wk avg" dash />
        </div>
      </div>
      <div className="relative h-40">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full pointer-events-none">
          {avg12Points && (
            <polyline points={avg12Points} fill="none" stroke={PALE} strokeWidth="1" strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
          )}
          {avg4Points && (
            <polyline points={avg4Points} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          )}
        </svg>
        <div className="flex items-end gap-1 h-full">
          {chrono.map((r, i) => {
            const v = pick(r);
            const n = v === null ? 0 : Number(v);
            const pct = max === 0 ? 0 : (n / max) * 100;
            return (
              <div
                key={r.week_ending}
                data-testid={`${testId}-bar`}
                className="flex-1 h-full flex flex-col justify-end"
                title={`Week ending ${formatWeekEnd(r.week_ending)}: ${money(v)}`}
              >
                <div className="w-full rounded-t-tight" style={{ height: `${pct}%`, backgroundColor: color, opacity: i === chrono.length - 1 ? 1 : 0.55 }} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-1 mt-1.5">
        {chrono.map((r) => (
          <div key={r.week_ending} className="flex-1 text-center text-[10px] leading-tight text-ink-soft">
            {formatWeekEnd(r.week_ending)}
          </div>
        ))}
      </div>
      <p data-testid={`${testId}-stats`} className="mt-2 text-xs text-ink-soft tabular-nums">
        12-wk avg {moneyNum(stats.avg12 ?? 0)} · last 4 wks {moneyNum(stats.last4Avg ?? 0)} vs prior 4{' '}
        {moneyNum(stats.prior4Avg ?? 0)} ({signedPct(stats.prior4DeltaPct)}) · trend{' '}
        {stats.trendPerWeek === null ? '–' : `${stats.trendPerWeek >= 0 ? '+' : ''}${moneyNum(stats.trendPerWeek)}/wk`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Monthly line chart (actual / plan / prior year) from sections.monthly
// ---------------------------------------------------------------------

export function MonthlyLineChart({ monthly, testId }: { monthly: OperatingMonthlyPoint[]; testId: string }): ReactNode {
  if (monthly.length === 0) return null;
  const pts = [...monthly].sort((a, b) => (a.month < b.month ? -1 : 1));
  const numOr = (v: string | null): number | null => (v === null ? null : Number.isFinite(Number(v)) ? Number(v) : null);
  const actual = pts.map((p) => numOr(p.actual));
  const plan = pts.map((p) => numOr(p.plan));
  const prior = pts.map((p) => numOr(p.prior_year));
  const all = [...actual, ...plan, ...prior].filter((v): v is number => v !== null);
  const max = all.length ? Math.max(...all) : 0;
  const min = Math.min(0, ...all);

  return (
    <div data-testid={testId} className={CARD}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-sage-800">Monthly · actual vs plan vs prior year</h2>
        <div className="flex items-center gap-3 text-[11px] text-ink-soft">
          <LegendDot color={FOREST} label="Actual" />
          <LegendDot color={GOLD} label="Plan" dash />
          <LegendDot color={PALE} label="Prior year" />
        </div>
      </div>
      <div className="h-48">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
          {points(prior, min, max) && (
            <polyline points={points(prior, min, max)} fill="none" stroke={PALE} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          )}
          {points(plan, min, max) && (
            <polyline points={points(plan, min, max)} fill="none" stroke={GOLD} strokeWidth="1.5" strokeDasharray="4 2" vectorEffect="non-scaling-stroke" />
          )}
          {points(actual, min, max) && (
            <polyline points={points(actual, min, max)} fill="none" stroke={FOREST} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          )}
        </svg>
      </div>
      <div className="flex gap-1 mt-1.5">
        {pts.map((p) => (
          <div key={p.month} className="flex-1 text-center text-[10px] leading-tight text-ink-soft">
            {monthLabel(p.month)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Clinician hours — CM table then SPICE; Clinician / Last week / 4-week avg /
// % of target / Flag (founder's column rework — the Trend sparkline and the
// Median DISPLAY column are gone; median lives on internally to feed the
// self-band flag fallback).
// ---------------------------------------------------------------------

// FLAG register (B.3 founder ruling): a clinician under / over their contracted
// hours is an ATTENTION matter, not an alarm — so out-of-band reads in GOLD
// (warm-700, the dashboard's secondary/identity gold, contrast-floor safe on
// paper), NOT pepper (pepper stays reserved for truly alarming clinical signal
// per the design language). ▲ high keeps forest (the dashboard's primary hue);
// ▼ low is gold; steady is muted. The "% of target" out-of-band cell echoes
// the same gold.
function FlagBadge({ flag }: { flag: ClinicianFlag | null }): ReactNode {
  if (flag === 'high') return <span className="text-sage-800 font-medium">▲ high</span>;
  if (flag === 'low') return <span className="text-warm-700 font-medium">▼ low</span>;
  if (flag === 'steady') return <span className="text-ink-soft">steady</span>;
  return <span className="text-ink-soft">–</span>;
}

/** One clinician-hours table (CM or SPICE). Both tables share the SAME column
 * grid — Clinician / Last week / 4-week avg / % of target / Flag — so the two
 * line up visually. CM carries live "% of target" cells (last week ÷
 * contracted target); SPICE has no contracted targets on file, so that column
 * renders EMPTY (header + cells) rather than being omitted, which keeps the
 * grids aligned per the founder's ask. `targets` is the buildTargetLookup
 * matcher (exact match first, then the pre-comma second chance for
 * credential suffixes). `sortRows` is the table-specific ordering — CM sorts
 * by % of target desc, SPICE by 4-week avg desc (see sortCmRows/sortSpiceRows
 * in operatingDerive.ts for the tiebreak rules). */
function ClinicianTable({
  label,
  list,
  targets,
  hasTargetColumn,
  sortRows,
  testId,
}: {
  label: string;
  list: ClinicianAgg[];
  targets: (name: string) => number | null;
  hasTargetColumn: boolean;
  sortRows: (rows: ClinicianTableRow[]) => ClinicianTableRow[];
  testId: string;
}): ReactNode {
  if (list.length === 0) return null;
  const tableRows = sortRows(buildClinicianRows(list, targets));
  return (
    <div data-testid={testId}>
      <h3 className="text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 mb-1">{label}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-sage-400 border-b border-sage-800/[8%]">
              <th className="py-1.5 pr-3">Clinician</th>
              <th className="py-1.5 px-3 text-right">Last week</th>
              <th className="py-1.5 px-3 text-right">4-week avg</th>
              <th className="py-1.5 px-3 text-right">{hasTargetColumn ? '% of target' : ''}</th>
              <th className="py-1.5 pl-3 text-right">Flag</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map(({ agg, display }) => (
              <tr key={agg.name} className="border-b border-sage-800/[5%] last:border-0">
                <td className="py-1.5 pr-3 text-ink">{agg.name}</td>
                <td className="py-1.5 px-3 text-right tabular-nums text-ink">{agg.lastWeek === null ? '–' : hours(String(agg.lastWeek))}</td>
                <td className="py-1.5 px-3 text-right tabular-nums text-ink-soft">{agg.avg4 === null ? '–' : hours(String(agg.avg4))}</td>
                <td
                  data-testid={`${testId}-pct`}
                  className={`py-1.5 px-3 text-right tabular-nums ${hasTargetColumn && display.utilizationOutOfBand ? 'text-warm-700 font-medium' : 'text-ink'}`}
                >
                  {hasTargetColumn ? (display.utilizationPct === null ? '–' : `${Math.round(display.utilizationPct)}%`) : ''}
                </td>
                <td className="py-1.5 pl-3 text-right">
                  <FlagBadge flag={display.flag} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ClinicianHoursCard({
  rows,
  note,
  rosterTargets,
  testId,
}: {
  rows: WeeklyOperatingMetricWire[];
  note?: string | null;
  rosterTargets?: OperatingRosterTarget[];
  testId: string;
}): ReactNode {
  const aggs = clinicianAggregates(rows);
  if (aggs.length === 0) return null;
  const cm = aggs.filter((a) => a.role === 'cm');
  const spice = aggs.filter((a) => a.role === 'spice');
  const targets = buildTargetLookup(rosterTargets ?? []);
  return (
    <Card testId={testId} title="Clinician hours">
      <div className="space-y-4">
        <ClinicianTable
          label="Care management"
          list={cm}
          targets={targets}
          hasTargetColumn
          sortRows={sortCmRows}
          testId={`${testId}-cm`}
        />
        <ClinicianTable
          label="SPICE"
          list={spice}
          targets={targets}
          hasTargetColumn={false}
          sortRows={sortSpiceRows}
          testId={`${testId}-spice`}
        />
      </div>
      <div data-testid={`${testId}-legend`} className="mt-3 space-y-1 text-[11px] text-ink-soft">
        <p>
          &ldquo;% of target&rdquo; is the last full week as a share of the contracted weekly target. Flag with a target
          on file: ▼ low = under 60% of target, ▲ high = over 110%, steady = within band.
        </p>
        <p>
          No target on file: ▼/▲ compare the last week to the clinician&apos;s own 12-week median (±25%), held back until
          3 weeks of data.
        </p>
      </div>
      {note && <p className="mt-3 text-xs text-ink-soft italic">{note}</p>}
    </Card>
  );
}

// ---------------------------------------------------------------------
// Top clients — highest-revenue this period (replaces Quiet clients, B.3)
// ---------------------------------------------------------------------

/** The service pills reuse the Chip primitive's identity idiom: CM = the sage
 * `service` variant (matches the dashboard's forest = CM law); SPICE = the
 * warm `specialty` variant (gold = SPICE). Category hues, no state meaning. */
function ServicePills({ services }: { services: OperatingTopClient['services'] }): ReactNode {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {services.map((s) => (
        <Chip key={s} variant={s === 'cm' ? 'service' : 'specialty'}>
          {s === 'cm' ? 'CM' : 'SPICE'}
        </Chip>
      ))}
    </span>
  );
}

export function TopClientsCard({ topClients, testId }: { topClients: OperatingTopClient[]; testId: string }): ReactNode {
  // Trust the payload order, but sort by total revenue desc defensively.
  const rows = [...topClients].sort((a, b) => {
    const av = a.total_revenue === null ? -Infinity : Number(a.total_revenue);
    const bv = b.total_revenue === null ? -Infinity : Number(b.total_revenue);
    return bv - av;
  });
  return (
    <Card testId={testId} title="Top clients · revenue this period">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 border-b border-sage-800/[8%]">
              <th className="py-2 pr-3">Client</th>
              <th className="py-2 px-3">Services</th>
              <th className="py-2 px-3 text-right">Total revenue</th>
              <th className="py-2 px-3 text-right">Avg / wk</th>
              <th className="py-2 pl-3 text-right">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.client_name}-${i}`} data-testid={`${testId}-row`} className="border-b border-sage-800/[5%] last:border-0">
                <td className="py-2 pr-3 text-ink">{r.client_name}</td>
                <td className="py-2 px-3">
                  <ServicePills services={r.services} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-ink font-semibold">{money(r.total_revenue)}</td>
                <td className="py-2 px-3 text-right tabular-nums text-ink-soft">{money(r.avg_weekly_revenue)}</td>
                <td className="py-2 pl-3 text-right tabular-nums text-ink-soft">{r.last_seen ?? '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------
// Sources footnote
// ---------------------------------------------------------------------

export function SourcesFootnote({ note, testId }: { note: string; testId: string }): ReactNode {
  return (
    <p data-testid={testId} className="text-[11px] text-ink-soft italic">
      {note}
    </p>
  );
}
